import type { DiagramProps } from '@/lib/scad-feature-types';
import { D } from './DiagramStyles';

export function CylinderDiagram({ values }: DiagramProps) {
  const h = Math.max(1, values.h ?? 10);
  const r1 = Math.max(0, values.r1 ?? 5);
  const r2 = Math.max(0, values.r2 ?? 5);
  const centered = values.center ?? true;
  const fn = Math.max(3, values.fn ?? 30);

  const cx = 110;
  // Normalize to SVG display space
  const maxDim = Math.max(h, r1 * 2, r2 * 2, 1);
  const scaleF = 80 / maxDim;
  const dH = Math.min(90, Math.max(15, h * scaleF));
  const dR1 = Math.min(55, Math.max(8, r1 * scaleF));
  const dR2 = Math.min(55, Math.max(4, r2 * scaleF));
  const ry = 9; // ellipse y-radius (perspective squish)

  const topY = centered ? 75 - dH / 2 : 75 - dH;
  const botY = centered ? 75 + dH / 2 : 75;

  // $fn arc on bottom ellipse
  const segAngle = (2 * Math.PI) / fn;
  const arcStartAngle = 0;
  const arcEndAngle = segAngle * 2;
  const fnAx1 = cx + dR1 * Math.cos(arcStartAngle);
  const fnAy1 = botY + ry * Math.sin(arcStartAngle);
  const fnAx2 = cx + dR1 * Math.cos(arcEndAngle);
  const fnAy2 = botY + ry * Math.sin(arcEndAngle);

  return (
    <svg viewBox="0 0 280 150" width="100%" style={{ display: 'block' }} aria-label="Cylinder diagram">
      <defs>
        <marker id="arr-c" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,1 L5,3 L0,5 Z" fill={D.DIM_STROKE} />
        </marker>
        <marker id="arr-c-rev" markerWidth="6" markerHeight="6" refX="1" refY="3" orient="auto">
          <path d="M5,1 L0,3 L5,5 Z" fill={D.DIM_STROKE} />
        </marker>
        <clipPath id="top-clip">
          <ellipse cx={cx} cy={topY} rx={dR2} ry={ry * 3} />
        </clipPath>
      </defs>

      {/* Body sides */}
      <line x1={cx - dR1} y1={botY} x2={cx - dR2} y2={topY}
            stroke={D.GEO_STROKE} strokeWidth="1.5" />
      <line x1={cx + dR1} y1={botY} x2={cx + dR2} y2={topY}
            stroke={D.GEO_STROKE} strokeWidth="1.5" />

      {/* Bottom cap fill */}
      <ellipse cx={cx} cy={botY} rx={dR1} ry={ry}
               fill={D.GEO_FILL} stroke={D.GEO_STROKE} strokeWidth="1.5" />
      {/* Bottom cap back arc (dashed) */}
      <path d={`M${cx - dR1},${botY} A${dR1},${ry} 0 0,1 ${cx + dR1},${botY}`}
            fill="none" stroke={D.GEO_STROKE} strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />

      {/* Top cap */}
      <ellipse cx={cx} cy={topY} rx={dR2} ry={ry}
               fill="rgba(96,165,250,0.1)" stroke={D.GEO_STROKE} strokeWidth="1.5" />

      {/* Height dimension */}
      <line x1={cx + Math.max(dR1, dR2) + 12} y1={botY}
            x2={cx + Math.max(dR1, dR2) + 12} y2={topY}
            stroke={D.Z_COLOR} strokeWidth="1" strokeDasharray={D.DIM_DASH}
            markerEnd="url(#arr-c)" markerStart="url(#arr-c-rev)" />
      <text x={cx + Math.max(dR1, dR2) + 20} y={(topY + botY) / 2 + 4}
            fontSize="10" fontFamily="monospace" fill={D.Z_COLOR} opacity="0.9">h={h}</text>

      {/* r1 dimension (bottom) */}
      <line x1={cx} y1={botY + ry + 6} x2={cx + dR1} y2={botY + ry + 6}
            stroke={D.X_COLOR} strokeWidth="1" strokeDasharray={D.DIM_DASH}
            markerEnd="url(#arr-c)" markerStart="url(#arr-c-rev)" />
      <text x={cx + dR1 / 2} y={botY + ry + 18} fontSize="10" fontFamily="monospace"
            fill={D.X_COLOR} textAnchor="middle" opacity="0.9">r1={r1}</text>

      {/* r2 dimension (top) — only if different */}
      {dR2 > 0 && (
        <>
          <line x1={cx} y1={topY - ry - 5} x2={cx + dR2} y2={topY - ry - 5}
                stroke={D.Y_COLOR} strokeWidth="1" strokeDasharray={D.DIM_DASH}
                markerEnd="url(#arr-c)" markerStart="url(#arr-c-rev)" />
          <text x={cx + dR2 / 2} y={topY - ry - 9} fontSize="10" fontFamily="monospace"
                fill={D.Y_COLOR} textAnchor="middle" opacity="0.9">r2={r2}</text>
        </>
      )}

      {/* $fn arc hint */}
      <path d={`M${fnAx1},${fnAy1} A${dR1},${ry} 0 0,1 ${fnAx2},${fnAy2}`}
            fill="none" stroke="#f59e0b" strokeWidth="2" opacity="0.7" />
      <text x="8" y="142" fontSize="9" fontFamily="monospace"
            fill="currentColor" opacity="0.45">$fn={fn} segments</text>

      {/* Center marker */}
      {centered && (
        <>
          <line x1={cx - 4} y1={75} x2={cx + 4} y2={75} stroke={D.ORIGIN_COLOR} strokeWidth="1" />
          <line x1={cx} y1={71} x2={cx} y2={79} stroke={D.ORIGIN_COLOR} strokeWidth="1" />
        </>
      )}

      {/* center label */}
      <text x="272" y="142" fontSize="9" fontFamily="monospace"
            fill="currentColor" opacity="0.45" textAnchor="end">
        {centered ? 'center=true' : 'center=false'}
      </text>
    </svg>
  );
}
